import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Loader from 'src/components/loader/Loader';
import API from 'src/utils/api';
import ExternalLinkIcon from 'src/icons/externalLink';
import CopyIcon from 'src/icons/copy';
import LineChart from 'src/components/line-chart/LineChart';
import { datesAreTheSameDay, dateToFormattedStr, isDateInPeriod } from 'src/utils/dates';
import CalendarIcon from 'src/icons/calendar';
import ChartFilterButton from 'src/components/chart-filter-button/ChartFilterButton';
import Select from 'src/components/select/Select';
import DatePickerRange from 'src/components/date-picker-range/DatePickerRange';
import SmallChartSection from 'src/components/small-chart-section/SmallChartSection';
import { ALL_SUPPORTED_TOKENS, NETWORK_NAME_LABELS, SUPPORTED_TOKENS } from 'src/utils/constants';
import Checkbox from 'src/components/checkbox/Checkbox';
import FilterDropdown from 'src/components/filter-dropdown/FilterDropdown';
import { copyToClipboard, truncate } from 'src/utils/strings';
import DatepickerDropdownFilter from 'src/components/datepicker-dropdown-filter/DatepickerDropdownFilter';
import Button from 'src/components/button/Button';
import Input from 'src/components/input/Input';
import SearchInput from 'src/components/search-input/SearchInput';
import Pagination from 'src/components/pagination/Pagination';
import exportIcon from 'src/icons/export.svg';
import { saveAs } from 'file-saver';
import ErrorState from 'src/components/error-state/ErrorState';
import PaymentsTable from 'src/components/payments-table/PaymentsTable';

const date30daysAgo = new Date();
date30daysAgo.setDate(date30daysAgo.getDate() - 30);

const DEFAULT_FILTERS = {
    startDate: date30daysAgo,
    endDate: new Date(),
};

const PERIOD_FILTERS = {
    WEEK: 'week',
    MONTH: 'month',
    YEAR: 'year',
    CUSTOM: 'custom',
};

const NETWORKS_ARRAY = Object.keys(SUPPORTED_TOKENS);

const ITEMS_PER_PAGE = 10;

const MerchantSales = () => {
    // Navigation
    const navigate = useNavigate();
    const { formId } = useParams();
    // Refs
    const fetchingFormsList = useRef(false);
    const fetchingChartData = useRef(false);
    const fetchingTransactions = useRef(false);
    const additionalDataContainer = useRef();

    // Chart Data
    const [formsList, setFormsList] = useState();
    const [paymentsChartData, setPaymentsChartData] = useState();
    const [totalPayments, setTotalPayments] = useState();
    const [salesNumber, setSalesNumber] = useState();
    const [numberOfSalesData, setNumberOfSalesData] = useState();
    const [customersNumber, setCustomersNumber] = useState();
    const [numberOfCustomersData, setNumberOfCustomersData] = useState();
    // Transactions Data
    const [transactions, setTransactions] = useState();
    // Filters
    const [selectedForm, setSelectedForm] = useState();
    const [selectedPeriodFilter, setSelectedPeriodFilter] = useState(PERIOD_FILTERS.MONTH);
    const [chartsFilters, setChartsFilters] = useState(DEFAULT_FILTERS);
    const [transactionsFilters, setTransactionsFilters] = useState(DEFAULT_FILTERS);
    const [minAmount, setMinAmount] = useState();
    const [maxAmount, setMaxAmount] = useState();
    const [productNames, setProductNames] = useState();
    const [totalTransactions, setTotalTransactions] = useState();
    const [transactionsPage, setTransactionsPage] = useState(1);
    const [transactionSearchTerm, setTransactionSearchTerm] = useState();
    // UI States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [screenWidth, setScreenWidth] = useState();

    useEffect(() => {
        if (typeof window !== 'undefined' && !screenWidth) {
            setScreenWidth(window.innerWidth);
        }
    }, [screenWidth]);

    useEffect(() => {
        if (!formsList || !formsList.length) return;
        if (!formId) return setSelectedForm(formsList[0]);
        const newSelectedForm = formsList.find((form) => form._id === formId);
        if (newSelectedForm) {
            setSelectedForm(newSelectedForm);
        }
    }, [formId, formsList]);

    useEffect(() => {
        if (fetchingFormsList.current) return; // make sure to fetch only at first render

        const fetchFormsList = async () => {
            fetchingFormsList.current = true;
            try {
                const response = await API.get('/payment-forms/list');
                setFormsList(response.data.paymentForms);
                fetchingFormsList.current = false;
            } catch (error) {
                if (error.response?.status === 404) {
                    toast.error('Page not found');
                } else {
                    toast.error('An unexpected error occured, please try again...');
                }
                setError(true);
                fetchingFormsList.current = false;
            }
        };

        fetchFormsList();
    }, []);

    useEffect(() => {
        if (fetchingChartData.current) return; // make sure to fetch only at first render
        if (!selectedForm) return;

        const fetchChartData = async () => {
            fetchingChartData.current = true;
            try {
                const response = await API.post(`/payments/chart-data/${selectedForm._id}`, {
                    startDate: dateToFormattedStr(DEFAULT_FILTERS.startDate),
                    endDate: dateToFormattedStr(DEFAULT_FILTERS.endDate),
                });
                // Main chart
                setPaymentsChartData(response.data.paymentsAmountData);
                setTotalPayments(response.data.totalPayments);
                // Number of sales chart
                setNumberOfSalesData(response.data.numberOfSalesData);
                setSalesNumber(response.data.salesNumber);
                // Number of customers chart
                setNumberOfCustomersData(response.data.numberOfCustomersData);
                setCustomersNumber(response.data.customersNumber);

                fetchingChartData.current = false;
            } catch (error) {
                console.log(error);
                toast.error('An unexpected error occured, please try again...');
                setError(true);
                fetchingChartData.current = false;
            }
        };

        fetchChartData();
    }, [selectedForm]);

    useEffect(() => {
        if (fetchingTransactions.current) return; // make sure to fetch only at first render
        if (!selectedForm) return;

        const fetchTransactions = async () => {
            fetchingTransactions.current = true;
            try {
                const response = await API.post(`/payments/list/${selectedForm._id}`, {
                    startDate: dateToFormattedStr(DEFAULT_FILTERS.startDate),
                    endDate: dateToFormattedStr(DEFAULT_FILTERS.endDate),
                });
                setTransactions(response.data.payments);
                setTotalTransactions(response.data.total);

                fetchingTransactions.current = false;
            } catch (error) {
                console.log(error);
                toast.error('An unexpected error occured, please try again...');
                setError(true);
                fetchingTransactions.current = false;
            }
        };

        fetchTransactions();
    }, [selectedForm]);

    useEffect(() => {
        if ((paymentsChartData && formsList && transactions) || error || formsList?.length === 0) {
            setLoading(false);
        }
    }, [error, formsList, paymentsChartData, transactions]);

    const refreshPaymentsData = useCallback(async () => {
        if (!selectedForm) return;
        try {
            setLoading(true);
            const response = await API.post(`/payments/chart-data/${selectedForm._id}`, {
                startDate: dateToFormattedStr(chartsFilters.startDate),
                endDate: dateToFormattedStr(chartsFilters.endDate),
            });
            // Main chart
            setPaymentsChartData(response.data.paymentsAmountData);
            setTotalPayments(response.data.totalPayments);
            // Number of sales chart
            setNumberOfSalesData(response.data.numberOfSalesData);
            setSalesNumber(response.data.salesNumber);
            // Number of customers chart
            setNumberOfCustomersData(response.data.numberOfCustomersData);
            setCustomersNumber(response.data.customersNumber);

            setLoading(false);
        } catch (error) {
            console.log(error);
            toast.error('An unexpected error occured, please try again...');
            setError(true);
            setLoading(false);
        }
    }, [chartsFilters, selectedForm]);

    const refreshChartData = useCallback(() => {
        // Main chart
        const newChartData = paymentsChartData.filter((data) =>
            isDateInPeriod(new Date(data.date), chartsFilters.startDate, chartsFilters.endDate)
        );
        setPaymentsChartData(newChartData);
        setTotalPayments(newChartData.reduce((acc, val) => acc + val.value, 0));
        // Number of sales chart
        const newSalesChartData = numberOfSalesData.filter((data) =>
            isDateInPeriod(new Date(data.date), chartsFilters.startDate, chartsFilters.endDate)
        );
        setNumberOfSalesData(newSalesChartData);
        setSalesNumber(newSalesChartData.reduce((acc, val) => acc + val.value, 0));
        // Number of customers chart
        const newCustomersChartData = numberOfSalesData.filter((data) =>
            isDateInPeriod(new Date(data.date), chartsFilters.startDate, chartsFilters.endDate)
        );
        setNumberOfCustomersData(newCustomersChartData);
        setCustomersNumber(newCustomersChartData.reduce((acc, val) => acc + val.value, 0));
    }, [chartsFilters, numberOfSalesData, paymentsChartData]);

    useEffect(() => {
        if (chartsFilters && paymentsChartData?.length) {
            // if the chartsFilters period is the same as the chart data period, then we don't need to do anything
            if (
                datesAreTheSameDay(chartsFilters.startDate, new Date(paymentsChartData[0].date)) &&
                datesAreTheSameDay(chartsFilters.endDate, new Date(paymentsChartData.at(-1).date))
            ) {
                return;
            }
            // if the startDate and endDate in the chartsFilters are in the paymentsChartData period
            // then we don't need to refresh the data, we just need to create new chart data based on the payments list we have
            if (
                isDateInPeriod(
                    chartsFilters.startDate,
                    new Date(paymentsChartData[0].date),
                    new Date(paymentsChartData.at(-1).date)
                ) &&
                isDateInPeriod(
                    chartsFilters.endDate,
                    new Date(paymentsChartData[0].date),
                    new Date(paymentsChartData.at(-1).date)
                )
            ) {
                refreshChartData();
                return;
            }
            // at this point, the chartsFilters and chart data periods are not the exact same and the chartsFilters period is not inside the
            // chart data period so we refresh our data and create new chart data
            refreshPaymentsData();
        }
    }, [chartsFilters, paymentsChartData, refreshChartData, refreshPaymentsData, selectedPeriodFilter]);

    const onSelectedFormChange = (e) => {
        const newSelectedForm = formsList.find((form) => form.name === e.target.value);
        navigate(`/merchant/${newSelectedForm._id}`);
    };

    const onCopyIDClick = () => {
        copyToClipboard(selectedForm._id);
    };

    const chartWidth = useMemo(() => {
        if (screenWidth < 1024) return screenWidth - 32;
        // if above max width then calculate based on max width and not screen width
        return screenWidth > 1536 ? (1536 - 96) * 0.65 : (screenWidth - 96) * 0.65;
    }, [screenWidth]);

    const getSalesPeriodTitle = () => {
        if (selectedPeriodFilter === PERIOD_FILTERS.MONTH) return 'for the last 30 days';
        if (selectedPeriodFilter === PERIOD_FILTERS.WEEK) return 'for the last 7 days';
        if (selectedPeriodFilter === PERIOD_FILTERS.YEAR) return 'for the last 365 days';
        return `from ${dateToFormattedStr(new Date(chartsFilters.startDate)) ?? ''} to ${
            dateToFormattedStr(new Date(chartsFilters.endDate)) ?? ''
        }`;
    };

    const onSelectedPeriodChange = (period) => {
        const startDate = new Date();
        switch (period) {
            case PERIOD_FILTERS.WEEK:
                startDate.setDate(startDate.getDate() - 7);
                break;
            case PERIOD_FILTERS.MONTH:
                startDate.setDate(startDate.getDate() - 30);
                break;
            case PERIOD_FILTERS.YEAR:
                startDate.setDate(startDate.getDate() - 365);
                break;
            default:
                break;
        }
        if (period !== PERIOD_FILTERS.CUSTOM) {
            setChartsFilters({
                ...chartsFilters,
                startDate: startDate,
                endDate: new Date(),
            });
        }
        setSelectedPeriodFilter(period);
    };

    const onCustomDatesChange = (startDate, endDate) => {
        setChartsFilters({
            ...chartsFilters,
            startDate: startDate,
            endDate: endDate,
        });
    };

    const onCoinsFilterChange = (coin, isSelected) => {
        const newCoinsFilter = isSelected
            ? [...(transactionsFilters?.coins ?? []), coin]
            : transactionsFilters.coins.filter((c) => c !== coin);
        setTransactionsFilters({
            ...transactionsFilters,
            coins: newCoinsFilter.length ? newCoinsFilter : undefined,
        });
    };

    const onNetworksFilterChange = (network, isSelected) => {
        const newNetworksFilter = isSelected
            ? [...(transactionsFilters?.networks ?? []), network]
            : transactionsFilters.networks.filter((c) => c !== network);
        setTransactionsFilters({
            ...transactionsFilters,
            networks: newNetworksFilter.length ? newNetworksFilter : undefined,
        });
    };

    const onTransactionsPeriodChange = (startDate, endDate) => {
        setTransactionsFilters({
            ...transactionsFilters,
            startDate,
            endDate,
        });
    };

    const onResetTransactionFilters = (filters) => {
        const newFilters = { ...transactionsFilters };
        filters
            .filter((f) => transactionsFilters[f])
            .forEach((filter) => {
                delete newFilters[filter];
            });
        setTransactionsFilters(newFilters);
    };

    const getAmountFilterText = () => {
        return transactionsFilters.minAmount && !transactionsFilters.maxAmount
            ? `Min $${transactionsFilters.minAmount}`
            : !transactionsFilters.minAmount && transactionsFilters.maxAmount
            ? `Max $${transactionsFilters.maxAmount}`
            : `$${transactionsFilters.minAmount} - $${transactionsFilters.maxAmount}`;
    };

    const onApplyAmountFilters = (e, setOpen) => {
        e.preventDefault();
        setOpen(false);
        setTransactionsFilters({
            ...transactionsFilters,
            minAmount: minAmount && minAmount !== '' ? minAmount : undefined,
            maxAmount: maxAmount && maxAmount !== '' ? maxAmount : undefined,
        });
    };

    const onApplyProductFilters = (e, setOpen) => {
        e.preventDefault();
        setOpen(false);
        setTransactionsFilters({
            ...transactionsFilters,
            productNames: productNames && productNames !== '' ? productNames : undefined,
        });
    };

    const onTransactionSearchClick = () => {
        if (!transactionSearchTerm && transactionSearchTerm !== '') return;
        setTransactionsFilters({ ...transactionsFilters, searchTerm: transactionSearchTerm });
    };

    useEffect(() => {
        if (!selectedForm) return;

        if (fetchingTransactions.current) return;

        const fetchTransactions = async () => {
            fetchingTransactions.current = true;
            try {
                const response = await API.post(`/payments/list/${selectedForm._id}`, {
                    ...transactionsFilters,
                    startDate: transactionsFilters.startDate
                        ? dateToFormattedStr(transactionsFilters.startDate)
                        : undefined,
                    endDate: transactionsFilters.endDate ? dateToFormattedStr(transactionsFilters.endDate) : undefined,
                    productNames: transactionsFilters.productNames
                        ? transactionsFilters.productNames.split(',')
                        : undefined,
                    page: transactionsPage,
                    itemsPerPage: ITEMS_PER_PAGE,
                });
                setTransactions(response.data.payments);
                setTotalTransactions(response.data.total);

                fetchingTransactions.current = false;
            } catch (error) {
                console.log(error);
                toast.error('An unexpected error occured, please try again...');
                setError(true);
                fetchingTransactions.current = false;
            }
        };

        fetchTransactions();
    }, [selectedForm, transactionsFilters, transactionsPage]);

    const onExportClick = async () => {
        setExportLoading(true);
        try {
            const response = await API.post(
                `/payments/download-list/${selectedForm._id}`,
                {
                    ...transactionsFilters,
                    startDate: transactionsFilters.startDate
                        ? dateToFormattedStr(transactionsFilters.startDate)
                        : undefined,
                    endDate: transactionsFilters.endDate ? dateToFormattedStr(transactionsFilters.endDate) : undefined,
                    productNames: transactionsFilters.productNames
                        ? transactionsFilters.productNames.split(',')
                        : undefined,
                },
                { responseType: 'blob' }
            );
            saveAs(response.data, 'payments.csv');
            setExportLoading(false);
        } catch (error) {
            console.log(error);
            toast.error('An unexpected error occured, please try again...');
            setError(true);
            setExportLoading(false);
        }
    };

    if (formsList?.length === 0) {
        return (
            <div className="h-screen w-screen flex justify-center items-center">
                <div className="lg:w-1/2 w-screen lg:border-2 border-gray-dark rounded-xl py-8 flex justify-center items-center flex-col">
                    <strong className="text-center text-lg">You don't have any payment forms</strong>
                    <p className="w-2/3 text-center text-gray-darker py-4">
                        Click the button below to create a payment form and start accepting crypto payments.
                    </p>
                    <Button
                        text="Create new payment form"
                        className="px-4"
                        primary
                        onClick={() => {
                            navigate('/merchant/create');
                        }}
                    />
                </div>
            </div>
        );
    }

    if ((loading || !selectedForm) && !error) {
        return (
            <div className="h-screen w-screen flex justify-center items-center">
                <Loader />
            </div>
        );
    }

    if (error) {
        return <ErrorState />;
    }

    return (
        <div className="pt-20 px-4 lg:px-12 max-w-screen-2xl mx-auto">
            <div className="w-full flex justify-between items-center">
                <div>
                    <span className="text-3xl">{selectedForm.name}</span>
                    <button onClick={onCopyIDClick} className="flex items-center mt-4 text-xs text-gray-darker">
                        Copy API ID <CopyIcon color="#838383" className="ml-1 w-4" />
                    </button>
                </div>
                <div className="flex flex-col">
                    <Select
                        label="Selected form"
                        fieldName="selectForm"
                        className="text-right mr-2 mb-2 w-48"
                        options={formsList.map((form) => form.name)}
                        onChange={onSelectedFormChange}
                        value={selectedForm.name}
                    />
                    <Link to="/merchant/create" className="underline text-right flex items-center justify-end">
                        Create new payment form <ExternalLinkIcon color="#000000" className="ml-1 mr-0 w-4" />
                    </Link>
                </div>
            </div>
            <div className="flex flex-col lg:flex-row mt-4">
                <div className="w-full lg:w-2/3 bg-gray-light rounded-xl px-4 pt-4">
                    <div className="lg:px-6 mb-4 flex justify-between">
                        <div className="flex flex-col">
                            <span className="font-bold text-xs lg:text-sm">Total payments {getSalesPeriodTitle()}</span>
                            <span className="font-bold text-base lg:text-2xl mt-1">${totalPayments}</span>
                        </div>
                        <div className="flex flex-wrap justify-end">
                            <ChartFilterButton
                                isFilterApplied={selectedPeriodFilter === PERIOD_FILTERS.WEEK}
                                onClick={() => onSelectedPeriodChange(PERIOD_FILTERS.WEEK)}
                                className="ml-2"
                            >
                                W
                            </ChartFilterButton>
                            <ChartFilterButton
                                isFilterApplied={selectedPeriodFilter === PERIOD_FILTERS.MONTH}
                                onClick={() => onSelectedPeriodChange(PERIOD_FILTERS.MONTH)}
                                className="ml-2"
                            >
                                M
                            </ChartFilterButton>
                            <ChartFilterButton
                                isFilterApplied={selectedPeriodFilter === PERIOD_FILTERS.YEAR}
                                onClick={() => onSelectedPeriodChange(PERIOD_FILTERS.YEAR)}
                                className="ml-2"
                            >
                                Y
                            </ChartFilterButton>
                            <ChartFilterButton
                                isFilterApplied={selectedPeriodFilter === PERIOD_FILTERS.CUSTOM}
                                onClick={() => onSelectedPeriodChange(PERIOD_FILTERS.CUSTOM)}
                                className="ml-2 relative flex items-center justify-center"
                            >
                                <DatePickerRange
                                    className="absolute"
                                    onDatesChange={onCustomDatesChange}
                                    initialStartDate={chartsFilters.startDate}
                                    initialEndDate={chartsFilters.endDate}
                                >
                                    <CalendarIcon
                                        color={selectedPeriodFilter === PERIOD_FILTERS.CUSTOM ? '#E41689' : '#838383'}
                                    />
                                </DatePickerRange>
                            </ChartFilterButton>
                        </div>
                    </div>
                    {/* if chart width is less that 768 - 32 then screen is less than 768 then the height is computed differently */}
                    <LineChart
                        width={chartWidth}
                        height={chartWidth < 768 - 32 ? chartWidth * 0.6 : chartWidth * 0.35}
                        color="#E41689"
                        data={paymentsChartData}
                    />
                </div>
                <div
                    className="w-full lg:w-1/3 lg:bg-gray-light rounded-xl lg:pt-4 lg:px-4 flex flex-col justify-around lg:mx-6"
                    ref={additionalDataContainer}
                >
                    <SmallChartSection
                        chartName="Number of sales"
                        title={salesNumber}
                        data={numberOfSalesData}
                        color="#0CE5D2"
                        className="h-48 lg:h-1/2 mt-8 lg:mt-0"
                    />
                    <SmallChartSection
                        chartName="Number of customers"
                        title={customersNumber}
                        data={numberOfCustomersData}
                        color="#008EC8"
                        className="h-48 lg:h-1/2 mt-4 lg:mt-0"
                    />
                </div>
            </div>
            <div className="w-full mt-6">
                <div className="flex flex-wrap justify-between items-center">
                    <span className="font-bold text-xl">Transactions</span>
                    <div className="flex items-center">
                        <button onClick={onExportClick} className="mt-2">
                            {exportLoading ? 'Exporting payments...' : <img src={exportIcon} alt="export" />}
                        </button>
                        <SearchInput
                            placeholder="Find a payment with the client's email"
                            className="w-72 lg:w-96 mb-0 ml-6"
                            value={transactionSearchTerm}
                            setValue={setTransactionSearchTerm}
                            displayButton
                            onSearchClick={onTransactionSearchClick}
                        />
                    </div>
                </div>
                <div className="mt-4 flex flex-wrap">
                    <FilterDropdown
                        placeholder="Coin"
                        text={transactionsFilters.coins && truncate(transactionsFilters.coins.join(', '), 14)}
                        showPlaceholder={!transactionsFilters.coins?.length}
                        onResetClick={() => onResetTransactionFilters(['coins'])}
                        contentClassName="bg-white"
                        className="ml-2 sm:ml-0"
                    >
                        {() =>
                            ALL_SUPPORTED_TOKENS.map((option, i) => (
                                <div key={i}>
                                    <button
                                        className="flex justify-between items-center w-full py-3 px-4"
                                        onClick={() =>
                                            onCoinsFilterChange(option, !transactionsFilters.coins.includes(option))
                                        }
                                        type="button"
                                    >
                                        <span className="flex items-center">
                                            <img
                                                src={`/crypto-icons/${option}.svg`}
                                                alt={option}
                                                width={25}
                                                className="mr-2"
                                            />
                                            {option}
                                        </span>
                                        <Checkbox
                                            fieldName={option}
                                            checked={
                                                transactionsFilters.coins && transactionsFilters.coins.includes(option)
                                            }
                                            onChange={(selected) => onCoinsFilterChange(option, selected)}
                                        />
                                    </button>
                                    {i !== ALL_SUPPORTED_TOKENS.length - 1 && <div className="separator"></div>}
                                </div>
                            ))
                        }
                    </FilterDropdown>
                    <FilterDropdown
                        className="ml-2"
                        placeholder="Network"
                        text={transactionsFilters.networks && truncate(transactionsFilters.networks.join(', '), 14)}
                        showPlaceholder={!transactionsFilters.networks?.length}
                        onResetClick={() => onResetTransactionFilters(['networks'])}
                        contentClassName="bg-white"
                    >
                        {() =>
                            NETWORKS_ARRAY.map((option, i) => (
                                <div key={i}>
                                    <button
                                        className="flex justify-between items-center w-full py-3 px-4"
                                        onClick={() =>
                                            onNetworksFilterChange(
                                                option,
                                                !transactionsFilters.networks.includes(option)
                                            )
                                        }
                                        type="button"
                                    >
                                        <span className="text-left">{NETWORK_NAME_LABELS[option]}</span>
                                        <Checkbox
                                            className=" w-15"
                                            fieldName={option}
                                            checked={
                                                transactionsFilters.networks &&
                                                transactionsFilters.networks.includes(option)
                                            }
                                            onChange={(selected) => onNetworksFilterChange(option, selected)}
                                        />
                                    </button>
                                    {i !== NETWORKS_ARRAY.length - 1 && <div className="separator"></div>}
                                </div>
                            ))
                        }
                    </FilterDropdown>
                    <DatepickerDropdownFilter
                        onDatesChange={onTransactionsPeriodChange}
                        startDate={transactionsFilters.startDate}
                        endDate={transactionsFilters.endDate}
                        onResetClick={() => onResetTransactionFilters(['startDate', 'endDate'])}
                    />
                    <FilterDropdown
                        className="ml-2"
                        placeholder="Amount"
                        text={
                            (transactionsFilters.minAmount || transactionsFilters.maxAmount) &&
                            truncate(getAmountFilterText(), 14)
                        }
                        showPlaceholder={!transactionsFilters.minAmount && !transactionsFilters.maxAmount}
                        onResetClick={() => onResetTransactionFilters(['minAmount', 'maxAmount'])}
                        contentClassName="w-96"
                    >
                        {(open, setOpen) => {
                            return (
                                <form className="bg-white py-4 px-4">
                                    <span className="font-bold">Amount range</span>
                                    <div className="flex justify-between items-center">
                                        <Input placeholder="Min amount" value={minAmount} setValue={setMinAmount} />
                                        <span className="mx-4">to</span>
                                        <Input placeholder="Max amount" value={maxAmount} setValue={setMaxAmount} />
                                    </div>
                                    <div className="flex justify-end items-center">
                                        <button
                                            className="mr-6"
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setOpen(false);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <Button
                                            text="Apply filter"
                                            primary
                                            className="w-36"
                                            type="button"
                                            onClick={(e) => onApplyAmountFilters(e, setOpen)}
                                        />
                                    </div>
                                </form>
                            );
                        }}
                    </FilterDropdown>
                    <FilterDropdown
                        className="ml-2"
                        placeholder="Product"
                        text={transactionsFilters.productNames && truncate(transactionsFilters.productNames, 14)}
                        showPlaceholder={!transactionsFilters.productNames}
                        onResetClick={() => onResetTransactionFilters(['productNames'])}
                        contentClassName="w-96"
                    >
                        {(open, setOpen) => (
                            <div className="bg-white py-4 px-4">
                                <SearchInput
                                    label='Product names (separated by ",")'
                                    placeholder="Product names"
                                    value={productNames}
                                    setValue={setProductNames}
                                />
                                <Button
                                    text="Apply filter"
                                    primary
                                    className="w-36 ml-auto"
                                    type="button"
                                    onClick={(e) => onApplyProductFilters(e, setOpen)}
                                />
                            </div>
                        )}
                    </FilterDropdown>
                </div>
                <PaymentsTable transactions={transactions} />
                <Pagination
                    page={transactionsPage}
                    setPage={(newPage) => setTransactionsPage(newPage)}
                    totalItems={totalTransactions}
                    itemsPerPage={ITEMS_PER_PAGE}
                    className="lg:bg-gray-light rounded-b-xl pt-8 mb-16"
                />
            </div>
        </div>
    );
};

export default MerchantSales;
